import { EmojiText } from '@gagovictor/shared-frontend-core/dist/shared/components/Emoji';
import { Box, LinearProgress, Typography } from '@mui/material';
import React from 'react';

import { MessageKey, useI18n } from '../../i18n';
import { Goal, isFunded, progressOf, raised } from '../goals';

/** The jar for one community goal: how full it is, and the public ledger behind the number. */
export const GoalMeter: React.FC<{ goal: Goal }> = ({ goal }) => {
    const { t, language } = useI18n();
    const money = (n: number) =>
        new Intl.NumberFormat(language, {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
        }).format(n);
    const funded = isFunded(goal);
    const prefix = goal.group === 'background' ? 'bg' : goal.group;
    const item = t(`opt.${prefix}.${goal.item}` as MessageKey);

    return (
        <Box
            sx={{
                p: 1.5,
                borderRadius: 3,
                border: 1,
                borderColor: 'divider',
            }}
            data-testid={`goal-${goal.id}`}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {t('goal.heading', { item })}
            </Typography>
            <LinearProgress
                variant="determinate"
                value={progressOf(goal) * 100}
                color={funded ? 'success' : 'primary'}
                aria-label={t('goal.heading', { item })}
                sx={{ height: 12, borderRadius: 6, my: 1 }}
            />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                <EmojiText>
                    {funded
                        ? t('goal.funded')
                        : t('goal.progress', {
                              raised: money(raised(goal)),
                              target: money(goal.target),
                          })}
                </EmojiText>
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {t('goal.how')}
            </Typography>
            <Box component="details" sx={{ mt: 0.5, fontSize: 13 }}>
                <summary>{t('goal.ledger')}</summary>
                {goal.ledger.length === 0 ? (
                    <Typography variant="caption">{t('goal.empty')}</Typography>
                ) : (
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {goal.ledger.map((e, i) => (
                            <li key={i}>
                                {e.date} · {money(e.amount)}
                                {e.note ? ` · ${e.note}` : ''}
                            </li>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};
